import {
  DayOfWeek,
  FlexDetails,
  FlexRange,
  Recurrence,
  RecurrenceType,
  TimeRange,
} from "@clockwise/schema/v2";
import { DateTime, Interval } from "luxon";

export const MAX_ATTENDEES_FOR_FLEX_LIMIT = 5;

export type EventForFlexibilityFromQuery = {
  externalEventId: string;
  flexibility: FlexDetails | null;
  isOutOfOffice?: boolean;
  dateOrTimeRange:
    | {
        __typename: "DateRange";
        dateRange: string;
      }
    | {
        __typename: "DateTimeRange";
        timeRange: string;
      };
  recurrence: Recurrence | null;
  timeDescription?: string;
  title: string;
  attendees?: AttendeeForFlexEvent[];
  canMarkFlexible: boolean;
};

export type AttendeeForFlexEvent = {
  person: {
    id: string;
    email: string;
    givenName?: string | null;
    familyName?: string | null;
    externalImageUrl?: string | null;
    isMe?: boolean;
  };
  isExternal?: boolean | null;
  isOrganizer: boolean | null;
  isOptional: boolean | null;
};

export type EventForFlexibility = {
  eventId: string;
  title: string;
  attendees: AttendeeForFlexEvent[];
  startTime: DateTime;
  isFlexible: boolean;
  flexRange: FlexRange;
  allowedDays: DayOfWeek[];
  timeOfDayFlexRange: {
    start: string;
    end: string;
  } | null;
  recurrenceText: string;
  recurrenceType: RecurrenceType | null;
};

export const getTimeRangeFromFlex = (timeRange?: TimeRange | null) => {
  if (!timeRange?.start || !timeRange?.end) return null;
  return {
    start: timeRange.start,
    end: timeRange.end,
  };
};

const parseRecurrenceText = (recurrenceObj: Recurrence | null, timeDescription?: string) => {
  if (timeDescription) return timeDescription;
  switch (recurrenceObj?.type) {
    case RecurrenceType.Daily:
      return "Daily";
    case RecurrenceType.Weekly:
      return "Weekly";
    case RecurrenceType.Fortnightly:
      return "every 2 weeks";
    case RecurrenceType.Monthly:
      return "Monthly";
    case RecurrenceType.LessThanMonthly:
      return "Other";
    default:
      return "";
  }
};

const filterOutMeetingsForFlexPages = (event: EventForFlexibilityFromQuery) => {
  // filter out:
  // - non editable meetings
  // - meetings over 5 attendees
  // - meeting with external attendees
  // - out of office events
  const hasExternalAttendees = event.attendees?.some((a) => a.isExternal);

  const isUnderAttendeeLimit = (event.attendees?.length || 0) < MAX_ATTENDEES_FOR_FLEX_LIMIT;

  return (
    event.canMarkFlexible && !event.isOutOfOffice && !hasExternalAttendees && isUnderAttendeeLimit
  );
};

export const parseAndFilterFlexibility = (
  events: EventForFlexibilityFromQuery[],
): EventForFlexibility[] => {
  return events
    .filter((e) => filterOutMeetingsForFlexPages(e))
    .map(
      ({
        externalEventId,
        flexibility,
        title,
        recurrence,
        timeDescription,
        dateOrTimeRange,
        attendees,
      }) => {
        return {
          eventId: externalEventId,
          title: title,
          attendees: attendees || [],
          startTime: Interval.fromISO(
            dateOrTimeRange.__typename === "DateRange"
              ? dateOrTimeRange.dateRange
              : dateOrTimeRange.timeRange,
          ).start,
          isFlexible: !!flexibility?.isFlexible,
          flexRange: flexibility?.flexRange || FlexRange.Day,
          allowedDays: flexibility?.allowedDays || [],
          timeOfDayFlexRange: getTimeRangeFromFlex(flexibility?.timeOfDayFlexRange),
          recurrenceText: parseRecurrenceText(recurrence, timeDescription),
          recurrenceType: recurrence?.type || null,
        };
      },
    );
};
